/* Serial Seal — artwork printability ADVISOR.
 *
 * ONE HARD RULE (owner 2026-07-28, "let them upload whatever as long as it can
 * be previewed"): if the browser can decode the file, we accept it. Nothing
 * else blocks the order.
 *
 * It used to block on file type, size, megapixels, animation, shape and DPI —
 * six ways to bounce a paying customer who is holding artwork they are happy
 * with. Every one of those is now a WARNING with an acknowledgement to tick,
 * so the customer is told exactly what will happen and still gets to buy. The
 * thresholds below therefore describe quality, not admission:
 *
 *   DPI_PASS 600   "sharp"
 *   DPI_MIN  200   below this we say it will look soft and ask them to confirm
 *   AR_TOL   ±10%  beyond this the crop is severe enough to spell out
 *
 * Accepting any decodable format is safe downstream because the builder
 * re-encodes what it keeps (downscale -> JPEG/PNG), so HEIC from an iPhone or
 * WebP from a screenshot never reaches the print pipeline as-is. That
 * re-encode is load-bearing: don't store the original bytes.
 *
 * The target depends on the chosen design AND orientation — a Medallion window
 * is not the shape of a Full Bleed seal — so ratio and pixel counts are read
 * from the live layout rather than hardcoded.
 *
 * Tiers: 'pass' | 'warning' | 'error'. 'error' now means only "unreadable
 * file". Warnings raise acknowledgements the customer ticks, which ride along
 * on the order as a record of what they were told.
 *
 * A message that raises an acknowledgement carries `ack: '<id>'`. The builder
 * uses it to print ONE of the pair rather than both — the acknowledgement
 * says the same thing in the customer's own voice and carries the same
 * numbers, so printing both was pure duplication and it read as a wall of
 * text on a phone. Nothing here is weakened by that: every ack still lands in
 * st[sk].acks and blockers() still requires every one of them.
 */
window.SS_UPLOAD = (function () {
  'use strict';

  var DPI_PASS = 600, DPI_MIN = 200;
  var MAX_MB = 20, MAX_BYTES = MAX_MB * 1024 * 1024, MIN_BYTES = 10 * 1024;
  var AR_TOL = 0.10;
  var MAX_MP = 100;

  /* The printable target for the current design + orientation, in inches. */
  function target(design, orientation) {
    var hasQr = !!(design.supports && design.supports.qr);
    var L = window.SS_SEAL.layout(orientation, design, hasQr, undefined,
        undefined, undefined, undefined,
        (typeof window.st === 'object' && window.st && window.st.sizeId) || undefined);
    /* Full-bleed artwork is supplied at the DIE size and bleeds under the rim,
       so quote the die (0.75 x 1.77 in) rather than the inset plate — quoting
       the plate produced an odd 1 : 2.52 next to a product sold as 1 : 2.36.
       Windowed designs quote their actual window. */
    var w = L.art.full ? L.W : L.art.w;
    var h = L.art.full ? L.H : L.art.h;
    /* layout() now composes NATIVELY in the selected die's mm (manual
       rebuild, 2026-08-30), so these boxes are already physical - no
       post-multiplication. */
    var wIn = w / 25.4, hIn = h / 25.4;
    return {
      wMm: w, hMm: h, wIn: wIn, hIn: hIn,
      ratio: w / h,
      minPx: [Math.ceil(wIn * DPI_MIN), Math.ceil(hIn * DPI_MIN)],
      bestPx: [Math.ceil(wIn * DPI_PASS), Math.ceil(hIn * DPI_PASS)]
    };
  }

  function fmtMB(b) { return (b / 1024 / 1024).toFixed(1); }
  function ratioStr(r) { return r >= 1 ? r.toFixed(2) + ' : 1' : '1 : ' + (1 / r).toFixed(2); }

  /* Is this an animated GIF/WebP/APNG? A single frame is what prints. */
  function isAnimated(file) {
    return new Promise(function (res) {
      var fr = new FileReader();
      fr.onload = function () {
        var b = new Uint8Array(fr.result), s = '';
        for (var i = 0; i < Math.min(b.length, 60000); i++) s += String.fromCharCode(b[i]);
        if (/^GIF8/.test(s)) return res((s.match(/\x00\x21\xF9\x04/g) || []).length > 1);
        if (s.indexOf('ANMF') > -1 || s.indexOf('acTL') > -1) return res(true);
        res(false);
      };
      fr.onerror = function () { res(false); };
      fr.readAsArrayBuffer(file.slice(0, 60000));
    });
  }

  /* Blank / solid-colour / transparency scan, same shape as VialArt's. */
  function pixelScan(img) {
    var c = document.createElement('canvas');
    var s = Math.min(140 / img.width, 140 / img.height, 1);
    c.width = Math.max(1, Math.round(img.width * s));
    c.height = Math.max(1, Math.round(img.height * s));
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, c.width, c.height);
    var d;
    try { d = ctx.getImageData(0, 0, c.width, c.height).data; } catch (_) { return null; }
    var n = 0, sum = 0, sq = 0, clear = 0, anyAlphaLt = false, total = c.width * c.height;
    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 8) { clear++; continue; }
      if (d[i + 3] < 250) anyAlphaLt = true;
      var lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      sum += lum; sq += lum * lum; n++;
    }
    if (!n) return { allAlpha0: true, std: 0, anyAlphaLt: true, clearPct: 1 };
    var mean = sum / n;
    return { allAlpha0: false, std: Math.sqrt(Math.max(0, sq / n - mean * mean)),
             anyAlphaLt: anyAlphaLt, clearPct: clear / total };
  }

  /* Validate a file against the current design + orientation.
     Resolves { tier, dpi, w, h, ratio, messages[], acks[], target } */
  function validate(file, design, orientation) {
    var T = target(design, orientation);
    var msgs = [], acks = [], tier = 'pass';
    function bump(t) { if (t === 'error') tier = 'error'; else if (t === 'warning' && tier !== 'error') tier = 'warning'; }
    function fail(text) {
      return Promise.resolve({ tier: 'error', dpi: null, messages: [{ tier: 'error', text: text }],
                               acks: [], target: T });
    }

    /* Rights gate (owner 2026-08-11): the included commercial license and
       copyright cover OUR designs only — anything uploaded prints under the
       CUSTOMER'S rights. This is a term of sale, not a validator finding,
       so it is pushed before any file check runs and applies to every
       accepted upload. Same id as the logo card's copy in sv-builder.js. */
    acks.push({ id: 'rights',
      label: 'I own this artwork or have the rights holder’s approval to print it.',
      sub: 'Our included commercial license covers Serial Seal designs only — uploaded artwork prints under your own rights.' });

    /* No format gate. Whatever the browser can decode, we can print — the
       builder re-encodes the copy it keeps, so an iPhone HEIC or a WebP
       screenshot never reaches the press in its original wrapper. */
    if (file.size > MAX_BYTES) {
      msgs.push({ tier: 'warning', text: 'That is a big file (' + fmtMB(file.size) +
        ' MB). It will be resized for printing, which may take a moment on a phone.' });
      bump('warning');
    }
    if (file.size < MIN_BYTES) {
      msgs.push({ tier: 'warning', text: 'That is a very small file (' +
        (file.size / 1024).toFixed(1) + ' KB) — check the preview looks how you expect.' });
      bump('warning');
    }

    return isAnimated(file).then(function (anim) {
      if (anim) {
        msgs.push({ tier: 'warning', text: 'This is an animated image — seals print the first frame.' });
        bump('warning');
        msgs[msgs.length - 1].ack = 'anim';
        acks.push({ id: 'anim', label: 'Print the first frame of my animation.',
          sub: 'A printed seal cannot move; only the frame in the preview is printed.' });
      }
      return new Promise(function (res, rej) {
        var url = URL.createObjectURL(file);
        var im = new Image();
        im.onload = function () { URL.revokeObjectURL(url); res(im); };
        // the ONLY hard failure: this browser cannot show it, so neither can we
        im.onerror = function () {
          URL.revokeObjectURL(url);
          rej(new Error("We couldn't open that file to preview it, so we can't print it either. " +
                        'Try saving it as a PNG or JPG and uploading again.'));
        };
        im.src = url;
      });
    }).then(function (im) {
      var w = im.naturalWidth, h = im.naturalHeight;
      var mp = (w * h) / 1e6;
      if (mp > MAX_MP) {
        msgs.push({ tier: 'warning', text: 'Very large image (' + mp.toFixed(0) +
          ' megapixels) — it will be scaled down to print.' });
        bump('warning');
      }

      /* DPI is whichever axis is worse against the printed size */
      var dpi = Math.floor(Math.min(w / T.wIn, h / T.hIn));
      var arImg = w / h;
      var arOff = Math.abs(arImg - T.ratio) / T.ratio;

      /* ——— shape ——— a wrong-shape file is not a broken file. It gets
         cropped to fill, so say exactly what disappears and let them decide
         against the preview, which shows the real crop. */
      if (arOff > AR_TOL) {
        var cutWhere = arImg < T.ratio ? 'top and bottom' : 'left and right sides';
        var cutPct = Math.round((1 - Math.min(arImg, T.ratio) / Math.max(arImg, T.ratio)) * 100);
        msgs.push({ tier: 'warning', text: 'Different shape to this seal — your file is ' +
          ratioStr(arImg) + ' and the seal prints at ' + ratioStr(T.ratio) + '. It is cropped to fill, ' +
          'so roughly ' + cutPct + '% is cut off the ' + cutWhere + '. Check the preview.' });
        bump('warning');
        msgs[msgs.length - 1].ack = 'offshape';
        acks.push({ id: 'offshape', label: 'I have checked the crop in the preview.',
          sub: 'About ' + cutPct + '% of the ' + cutWhere + ' will not print. For an exact fit, supply ' +
               ratioStr(T.ratio) + ' — around ' + T.bestPx[0] + ' × ' + T.bestPx[1] + ' px.' });
      }

      /* ——— resolution ——— */
      if (dpi >= DPI_PASS) {
        msgs.push({ tier: 'pass', text: 'Sharp resolution — ' + dpi + ' DPI at printed size.' });
      } else if (dpi >= DPI_MIN) {
        msgs.push({ tier: 'warning', text: 'A little low-res (' + dpi + ' DPI). It will print but may look ' +
          'slightly soft — ' + DPI_PASS + ' DPI is best (about ' + T.bestPx[0] + ' × ' + T.bestPx[1] + ' px).' });
        bump('warning');
        msgs[msgs.length - 1].ack = 'lowdpi';
        acks.push({ id: 'lowdpi', label: 'My artwork is low resolution and may print soft.',
          sub: 'It is ' + dpi + ' DPI at printed size. Fine lines and small text will lose detail.' });
      } else {
        msgs.push({ tier: 'warning', text: 'Low resolution — ' + dpi + ' DPI at printed size. It will ' +
          'print soft or blocky, especially fine lines and small text.' });
        bump('warning');
        msgs[msgs.length - 1].ack = 'verylowdpi';
        acks.push({ id: 'verylowdpi', label: 'Print it anyway — I accept it will look soft.',
          sub: 'Only ' + dpi + ' DPI at printed size (' + DPI_MIN + ' is our recommended minimum, ' +
               T.bestPx[0] + ' × ' + T.bestPx[1] + ' px is sharpest). Seals are made to order and ' +
               'cannot be remade free because the supplied artwork was small.' });
      }

      /* ——— near-shape trim ———
         Only when the severe branch above did NOT fire. Anything past AR_TOL
         tripped BOTH, pushing two acks with the same id 'offshape' — two
         checkboxes bound to one flag, so ticking either left the other looking
         unticked and the customer hunting for a box that was already ticked.
         Enforcement is unchanged: one offshape ack, still required. */
      if (arOff > 0.01 && arOff <= AR_TOL) {
        var trimPct = Math.round(arOff * 100);
        var where = arImg < T.ratio ? 'top and bottom' : 'left and right sides';
        msgs.push({ tier: 'warning', text: 'Close to the print shape (' + ratioStr(arImg) + ' vs ' +
          ratioStr(T.ratio) + '). The artwork fills the area, so about ' + trimPct +
          '% will be trimmed off the ' + where + '.' });
        bump('warning');
        msgs[msgs.length - 1].ack = 'offshape';
        acks.push({ id: 'offshape', label: 'Slight trim on the ' + where + ' is OK.',
          sub: 'My file is ' + ratioStr(arImg) + ', not ' + ratioStr(T.ratio) + ' — about ' + trimPct +
               '% of the ' + where + ' will not print. Keep anything important away from those edges.' });
      }

      /* ——— content scan ——— */
      var scan = pixelScan(im);
      if (scan) {
        if (scan.allAlpha0) {
          msgs.push({ tier: 'warning', text: 'This image is completely empty or transparent — the seal ' +
            'would print as bare holographic foil with no artwork on it.' });
          bump('warning');
          msgs[msgs.length - 1].ack = 'empty';
          acks.push({ id: 'empty', label: 'I know this image is blank.',
            sub: 'Nothing will print in the artwork area — it will be plain holographic foil.' });
        }
        if (scan.std < 2) {
          msgs.push({ tier: 'warning', text: 'This looks blank or a single solid colour. Double-check ' +
            'you uploaded the right file.' });
          bump('warning');
          msgs[msgs.length - 1].ack = 'blank';
          acks.push({ id: 'blank', label: 'This is the artwork I want printed.',
            sub: 'It looks blank or one solid colour — we will print it exactly as supplied.' });
        }
        if (scan.anyAlphaLt) {
          msgs.push({ tier: 'warning', text: 'Your artwork has transparent areas — on this material they ' +
            'print as bare holographic foil, not white.' });
          bump('warning');
          msgs[msgs.length - 1].ack = 'transp';
          acks.push({ id: 'transp', label: 'Transparent areas will show the bare foil.',
            sub: 'There is no white ink — see-through parts print as shiny holographic material.' });
        }
      }

      return { tier: tier, dpi: dpi, w: w, h: h, ratio: arImg, img: im,
               messages: msgs, acks: acks, target: T };
    }).catch(function (e) {
      return { tier: 'error', dpi: null, messages: [{ tier: 'error', text: e.message ||
               'We could not read that image.' }], acks: [], target: T };
    });
  }

  return { validate: validate, target: target, ratioStr: ratioStr,
           DPI_MIN: DPI_MIN, DPI_PASS: DPI_PASS, MAX_MB: MAX_MB, AR_TOL: AR_TOL };
})();
